import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Shippings } from '@pages/checkout-app/types';
import { Checkout } from '@pdqdev/pdq-shared/dist/interfaces';
import FeePerWeekday = Checkout.FeePerWeekday;
import FeePerWeekdayRule = Checkout.FeePerWeekdayRule;
import { CheckoutService } from '@pages/checkout-app/checkout.service';
import { countryList } from '../../configs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

export const WEEKDAY_OPTIONS = Object.entries(Checkout.ExtendedWeekdaysToDisplayNameMap).map(([key, value]) => {
  return {
    label: value,
    value: +key,
  };
});

@UntilDestroy()
@Component({
  selector: 'fee-per-weekday',
  templateUrl: './fee-per-weekday.component.html',
  styleUrls: ['./fee-per-weekday.component.scss'],
  standalone: false,
})
export class FeePerWeekdayComponent implements OnInit, OnChanges {
  @Input() formGroup: UntypedFormGroup;
  @Input() shopCurrency: any;
  @Input() loadedValues: Shippings;
  fieldSetName = 'feePerWeekday';
  formArrayFieldName = 'feePerWeekdayRules';
  readonly ExtendedWeekdays = Checkout.ExtendedWeekdays;

  weekdayOptions = WEEKDAY_OPTIONS;
  holidaysOptions = countryList('label');
  maxRulesToAdd = this.weekdayOptions.length;
  shippingMethodCurrency;

  constructor(
    private fb: UntypedFormBuilder,
    private checkoutService: CheckoutService,
  ) {}

  ngOnInit(): void {
    this.shippingMethodCurrency = this.checkoutService.shippingMethodCurrency$;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes?.loadedValues?.currentValue) {
      const feePerWeekday = changes.loadedValues.currentValue['feePerWeekday'];
      if (feePerWeekday) {
        this.handleLoadedValues(feePerWeekday);
      }
    }
  }

  handleLoadedValues(values: FeePerWeekday) {
    const feePerWeekdayFormGroup = this.getFromGroup(this.fieldSetName);
    feePerWeekdayFormGroup.controls['enabled'].setValue(values.enabled ?? false);

    values.feePerWeekdayRules?.forEach((rule) => {
      this.addRule(rule);
    });
  }

  getFormArray(): UntypedFormArray {
    return this.formGroup.get(this.fieldSetName + '.' + this.formArrayFieldName) as UntypedFormArray;
  }

  getFromGroup(formGroupName: string): UntypedFormGroup {
    return this.formGroup.get(formGroupName) as UntypedFormGroup;
  }

  removeRule(index: number) {
    this.getFormArray().removeAt(index);
  }

  onToggle(event) {
    event && this.getFormArray()?.value?.length == 0 && this.addRule();
  }

  handleDateRangeChange({ start, end }, control: UntypedFormControl) {
    control.setValue({ start, end });
  }

  addRule(loadedValues?: FeePerWeekdayRule) {
    const takenWeekdaysOptions = this.getFormArray().value.map((v) => +v.weekday);
    const availableFirstOption = this.weekdayOptions.find((o) => !takenWeekdaysOptions.includes(+o.value));
    const newRuleGroup = this.fb.group({
      amount: new UntypedFormControl(loadedValues?.amount ?? 0.1),
      weekday: new UntypedFormControl(loadedValues?.weekday ?? availableFirstOption.value),
      customDate: new UntypedFormControl({
        start: loadedValues?.customDate?.start,
        end: loadedValues?.customDate?.end,
      }),
      numOfDays: new UntypedFormControl(loadedValues?.numOfDays ?? 1),
      nationalHolidays: new UntypedFormGroup({
        country: new UntypedFormControl(
          loadedValues?.nationalHolidays?.country ?? null,
          loadedValues?.weekday === Checkout.ExtendedWeekdays.DaysAfterHolidays ? Validators.required : null,
        ),
        holidays: new UntypedFormControl(null),
      }),
      shippingName: new UntypedFormControl(loadedValues?.shippingName ?? null),
      shippingNotes: new UntypedFormControl(loadedValues?.shippingNotes ?? null),
    });

    newRuleGroup
      .get('weekday')
      .valueChanges.pipe(untilDestroyed(this))
      .subscribe((value) => {
        if (value === Checkout.ExtendedWeekdays.DaysAfterHolidays) {
          newRuleGroup.get('nationalHolidays.country').setValidators(Validators.required);
          newRuleGroup.get('nationalHolidays.country').updateValueAndValidity();
        } else {
          newRuleGroup.get('nationalHolidays.country').clearValidators();
          newRuleGroup.get('nationalHolidays.country').updateValueAndValidity();
        }
      });

    this.getFormArray().push(newRuleGroup);
  }

  getDefaultOption(list, value) {
    if (value != null && value >= 0) {
      return list.find((o) => o.value.toString() === value.toString());
    }
    return undefined;
  }
}
